// ModulesView.jsx - Modules management component
import React, { useState } from 'react';
import { 
  Plus, Search, Star, BookOpen, Video, Users, 
  ChevronRight, Edit, Trash2, Calendar, Eye 
} from 'lucide-react';
import apiService from '../../services/api';

const ModulesView = ({ modules, onModuleSelect, onRefresh, setError, setSuccess }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    icon: '📚'
  });

  const filteredModules = modules.filter(module => {
    const matchesSearch = module.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         module.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'active' && module.is_active) ||
                         (filterType === 'draft' && !module.is_active);
    
    return matchesSearch && matchesFilter;
  });

  const handleCreateModule = async (e) => {
    e.preventDefault();
    try {
      if (editingModule) {
        await apiService.updateModule(editingModule.id, moduleForm);
        setSuccess('Módulo actualizado exitosamente');
      } else {
        await apiService.createModule(moduleForm);
        setSuccess('Módulo creado exitosamente');
      }
      setShowModuleModal(false);
      resetForm();
      onRefresh();
    } catch (err) {
      setError(`Error al guardar módulo: ${err.message}`);
    }
  };

  const handleDeleteModule = async (moduleId, moduleTitle) => {
    if (!window.confirm(`¿Eliminar el módulo "${moduleTitle}" y todos sus capítulos?`)) {
      return;
    }
    
    try {
      await apiService.deleteModule(moduleId);
      setSuccess('Módulo eliminado exitosamente');
      onRefresh();
    } catch (err) {
      setError(`Error al eliminar módulo: ${err.message}`);
    }
  };

  const openEditModal = (module) => {
    setEditingModule(module);
    setModuleForm({
      title: module.title,
      description: module.description || '',
      icon: module.icon || '📚'
    });
    setShowModuleModal(true);
  };

  const resetForm = () => {
    setModuleForm({ title: '', description: '', icon: '📚' });
    setEditingModule(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Módulos y Contenido</h2>
          <p className="text-gray-600 mt-1">Gestiona los módulos de aprendizaje y su contenido</p>
        </div>
        <button 
          onClick={() => setShowModuleModal(true)}
          className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center space-x-2 shadow-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nuevo Módulo</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex space-x-4">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar módulos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select 
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Todos los módulos</option>
          <option value="active">Activos</option>
          <option value="draft">Borradores</option>
        </select>
      </div>

      {/* Modules Grid */}
      {filteredModules.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredModules.map(module => (
            <div key={module.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-3xl">{module.icon || '📚'}</div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(module);
                      }}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteModule(module.id, module.title);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {module.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {module.description || 'Sin descripción'}
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1 text-blue-600">
                      <BookOpen className="w-4 h-4" />
                      <span className="font-semibold">{module.total_chapters || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500">Capítulos</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1 text-purple-600">
                      <Video className="w-4 h-4" />
                      <span className="font-semibold">{module.video_count || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500">Videos</p>
                  </div>
                </div>
                
                {module.completion_rate !== undefined && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Progreso promedio</span>
                      <span className="font-medium">{module.completion_rate}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${module.completion_rate}%` }}
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end items-center">
                  <button
                    onClick={() => onModuleSelect(module)}
                    className="bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 flex items-center space-x-2 text-sm font-medium transition-colors"
                  >
                    <span>Gestionar</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterType !== 'all' ? 'No se encontraron módulos' : 'No hay módulos creados'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || filterType !== 'all' 
              ? 'Intenta cambiar los filtros de búsqueda'
              : 'Comienza creando tu primer módulo de aprendizaje'
            }
          </p>
          {!searchTerm && filterType === 'all' && (
            <button
              onClick={() => setShowModuleModal(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Crear Primer Módulo
            </button>
          )}
        </div>
      )}

      {/* Module Creation/Edit Modal */}
      {showModuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              {editingModule ? 'Editar Módulo' : 'Crear Nuevo Módulo'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título del Módulo
                </label>
                <input
                  type="text"
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Fundamentos de Inversión"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Describe el contenido y objetivos del módulo..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icono (Emoji)
                </label>
                <input
                  type="text"
                  value={moduleForm.icon}
                  onChange={(e) => setModuleForm({ ...moduleForm, icon: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="📚"
                  maxLength="2"
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModuleModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCreateModule}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingModule ? 'Actualizar' : 'Crear Módulo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModulesView;